import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ConversationMessage,
  ConversationQuery,
  ConversationSession,
} from './models/ai-conversation.model';
import { aiService } from '../services/aiService';

interface AiServicesState {
  conversations: ConversationSession[];
  loadingConversations: boolean;
  loadedConversations: boolean;

  loadingConversation: boolean;
  loadedConversation: boolean;

  selectedConversationID: string | null;

  conversationMessages: ConversationMessage[];

  creatingConversation: boolean;
  createdConversation: boolean;

  sendingMessage: boolean;
  sentMessage: boolean;
  sendingMessageContent: ConversationQuery | null;

  conversationResponseError: any;
  conversationCreationError: any;
}

const initialState: AiServicesState = {
  conversations: [],
  loadingConversations: false,
  loadedConversations: false,

  loadingConversation: false,
  loadedConversation: false,

  selectedConversationID: null,

  conversationMessages: [],

  creatingConversation: false,
  createdConversation: false,

  sendingMessage: false,
  sentMessage: false,
  sendingMessageContent: null,

  conversationResponseError: null,
  conversationCreationError: null,
};

const slice = createSlice({
  name: 'aiServices',
  initialState,
  reducers: {
    setConversations: (state, action: PayloadAction<ConversationSession[]>) => {
      state.conversations = action.payload;
    },
    setLoadingConversations: (state, action: PayloadAction<boolean>) => {
      state.loadingConversations = action.payload;
    },
    setLoadedConversations: (state, action: PayloadAction<boolean>) => {
      state.loadedConversations = action.payload;
    },
    setLoadingConversation: (state, action: PayloadAction<boolean>) => {
      state.loadingConversation = action.payload;
    },
    setLoadedConversation: (state, action: PayloadAction<boolean>) => {
      state.loadedConversation = action.payload;
    },
    setConversationMessages: (
      state,
      action: PayloadAction<ConversationMessage[]>,
    ) => {
      state.conversationMessages = action.payload;
    },
    setSelectedConversationID: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.selectedConversationID = action.payload;
    },
    setCreatingConversation: (state, action: PayloadAction<boolean>) => {
      state.creatingConversation = action.payload;
    },
    setCreatedConversation: (state, action: PayloadAction<boolean>) => {
      state.createdConversation = action.payload;
    },
    setSendingMessage: (state, action: PayloadAction<boolean>) => {
      state.sendingMessage = action.payload;
    },
    setSentMessage: (state, action: PayloadAction<boolean>) => {
      state.sentMessage = action.payload;
    },
    setSendingMessageContent: (
      state,
      action: PayloadAction<ConversationQuery | null>,
    ) => {
      state.sendingMessageContent = action.payload;
    },
    upsertConversation: (state, action: PayloadAction<ConversationSession>) => {
      state.conversations = [...state.conversations, action.payload];
    },
    upsertConversationMessage: (
      state,
      action: PayloadAction<ConversationMessage>,
    ) => {
      state.conversationMessages = [
        ...state.conversationMessages,
        action.payload,
      ];
    },
    setConversationResponseError: (state, action: PayloadAction<any>) => {
      state.conversationResponseError = action.payload;
    },
    setConversationCreationError: (state, action: PayloadAction<any>) => {
      state.conversationCreationError = action.payload;
    },
  },
});

// Conversations
export const { setConversations } = slice.actions;
export const { setLoadingConversations } = slice.actions;
export const { setLoadedConversations } = slice.actions;
export const { setLoadingConversation } = slice.actions;
export const { setLoadedConversation } = slice.actions;
export const { setConversationMessages } = slice.actions;
export const { setSelectedConversationID } = slice.actions;
export const { setCreatingConversation } = slice.actions;
export const { setCreatedConversation } = slice.actions;
export const { setSendingMessage } = slice.actions;
export const { setSentMessage } = slice.actions;
export const { setSendingMessageContent } = slice.actions;
export const { upsertConversation } = slice.actions;
export const { upsertConversationMessage } = slice.actions;
export const { setConversationResponseError } = slice.actions;
export const { setConversationCreationError } = slice.actions;

export const fetchConversationsAsync = () => (dispatch: Function) => {
  dispatch(setLoadingConversations(true));
  dispatch(setLoadedConversations(false));
  aiService.getConversations().subscribe({
    next: (conversations) => {
      dispatch(setConversations(conversations));
      dispatch(setLoadingConversations(false));
      dispatch(setLoadedConversations(true));
    },
    error: (err) => {
      dispatch(setLoadingConversations(false));
      dispatch(setLoadedConversations(false));
    },
  });
};

export const fetchConversationMessages =
  (conversationID: string) => (dispatch: Function) => {
    dispatch(setLoadingConversation(true));
    dispatch(setLoadedConversation(false));
    aiService.getConversationMessages(conversationID).subscribe({
      next: (conversation) => {
        dispatch(setSelectedConversationID(conversationID));
        dispatch(setConversationMessages(conversation.messages.reverse()));
        dispatch(setLoadingConversation(false));
        dispatch(setLoadedConversation(true));
      },
      error: (err) => {
        dispatch(setLoadingConversation(false));
        dispatch(setLoadedConversation(false));
      },
    });
  };

export const createConversationAsync = (data: any) => (dispatch: Function) => {
  dispatch(setCreatingConversation(true));
  dispatch(setCreatedConversation(false));
  dispatch(setSendingMessageContent(data));
  aiService.createConversationSession(data).subscribe({
    next: (conversation) => {
      dispatch(upsertConversation(conversation));
      dispatch(setSelectedConversationID(conversation.conversationSessionID));
      dispatch(setCreatingConversation(false));
      dispatch(setCreatedConversation(true));
      dispatch(setSendingMessageContent(null));
    },
    error: (err) => {
      dispatch(setCreatingConversation(false));
      dispatch(setCreatedConversation(false));
      dispatch(setConversationCreationError(err));
    },
  });
};

export const sendMessageAsync =
  (conversationID: string, message: ConversationQuery) =>
  (dispatch: Function) => {
    dispatch(setSendingMessage(true));
    dispatch(setSentMessage(false));
    dispatch(setSendingMessageContent(message));
    aiService.sendMessage(conversationID, message).subscribe({
      next: (message) => {
        dispatch(upsertConversationMessage(message));
        dispatch(setSendingMessage(false));
        dispatch(setSentMessage(true));
        dispatch(setSendingMessageContent(null));
      },
      error: (err) => {
        dispatch(setSendingMessage(false));
        dispatch(setSentMessage(false));
        dispatch(setSendingMessageContent(null));
        dispatch(setConversationResponseError(err));
      },
    });
  };

export const selectConversations = (state: { aiServices: AiServicesState }) =>
  state.aiServices.conversations;
export const selectLoadingConversations = (state: {
  aiServices: AiServicesState;
}) => state.aiServices.loadingConversations;
export const selectLoadedConversations = (state: {
  aiServices: AiServicesState;
}) => state.aiServices.loadedConversations;
export const selectLoadingConversation = (state: {
  aiServices: AiServicesState;
}) => state.aiServices.loadingConversation;
export const selectLoadedConversation = (state: {
  aiServices: AiServicesState;
}) => state.aiServices.loadedConversation;
export const selectConversationMessages = (state: {
  aiServices: AiServicesState;
}) => state.aiServices.conversationMessages;
export const selectSelectedConversationID = (state: {
  aiServices: AiServicesState;
}) => state.aiServices.selectedConversationID;
export const selectCreatingConversation = (state: {
  aiServices: AiServicesState;
}) => state.aiServices.creatingConversation;
export const selectCreatedConversation = (state: {
  aiServices: AiServicesState;
}) => state.aiServices.createdConversation;
export const selectSendingMessage = (state: { aiServices: AiServicesState }) =>
  state.aiServices.sendingMessage;
export const selectSentMessage = (state: { aiServices: AiServicesState }) =>
  state.aiServices.sentMessage;
export const selectSendingMessageContent = (state: {
  aiServices: AiServicesState;
}) => state.aiServices.sendingMessageContent;
export const selectConversationResponseError = (state: {
  aiServices: AiServicesState;
}) => state.aiServices.conversationResponseError;
export const selectConversationCreationError = (state: {
  aiServices: AiServicesState;
}) => state.aiServices.conversationCreationError;

export default slice.reducer;
